/**
 * Auth boundary.
 *
 * Wraps the app shell. Three states:
 *   - Initial /me fetch in flight → full-screen spinner
 *   - Authenticated → render children (the shell + outlet)
 *   - Unauthenticated → redirect to /login (handled via TanStack Router)
 *
 * The /login routes themselves bypass this boundary in router.tsx by sitting
 * outside the layout route that mounts the shell.
 */
import { useNavigate } from '@tanstack/react-router';
import { useEffect, type ReactNode } from 'react';
import { Spinner } from '@/components/ui/spinner';
import { useCurrentUser } from '@/lib/auth';

export function AuthBoundary({ children }: { children: ReactNode }) {
  const me = useCurrentUser();
  const navigate = useNavigate();

  useEffect(() => {
    if (me.isLoading) return;
    if (!me.data) {
      void navigate({ to: '/login', replace: true });
    }
  }, [me.isLoading, me.data, navigate]);

  if (me.isLoading || !me.data) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner className="h-6 w-6 text-secondary" />
      </div>
    );
  }

  return <>{children}</>;
}
